// components/EmailInput.tsx
// Reusable email input component with format validation

import React, { useState, useEffect } from 'react';
import { FiMail, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

/**
 * Props for EmailInput component
 */
export interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidityChange?: (valid: boolean) => void; // Optional callback for validity state
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string; // External error (overrides internal validation)
  autoFocus?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  id?: string;
  name?: string;
  validateOnBlur?: boolean; // Whether to validate on blur (default: true)
  title?: string; // Tooltip text
}

/**
 * Email format validation regex (same as backend)
 * Matches standard email format: local@domain.tld
 * Supports international domains including Israeli domains (.il, .co.il, etc.)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * List of fake/test email addresses to reject (case-insensitive)
 */
const FAKE_EMAILS = [
  'test@test.com',
  'example@example.com',
  'aa@aa.com',
  'test@test.test',
  'example@example.example',
];

/**
 * Client-side format validation (same rules as backend)
 *
 * @param email - Email address to validate
 * @returns Object with isValid boolean and optional error message
 */
export const validateEmailFormat = (email: string): { isValid: boolean; error?: string } => {
  const trimmed = email.trim();

  // Empty check
  if (!trimmed) {
    return { isValid: false, error: 'Email address is required' };
  }

  // 🔹 Max total email length (RFC-ish limit)
  if (trimmed.length > 254) {
    return {
      isValid: false,
      error: 'Email address is too long. Maximum length is 254 characters',
    };
  }

  // Reject spaces
  if (/\s/.test(trimmed)) {
    return { isValid: false, error: 'Email address cannot contain spaces' };
  }

  // Reject double dots
  if (trimmed.includes('..')) {
    return { isValid: false, error: 'Email address cannot contain consecutive dots' };
  }

  // Reject emails starting or ending with dot
  if (trimmed.startsWith('.') || trimmed.endsWith('.')) {
    return { isValid: false, error: 'Email address cannot start or end with a dot' };
  }

  // Reject fake/test emails
  const emailLower = trimmed.toLowerCase();
  if (FAKE_EMAILS.some(fake => emailLower === fake.toLowerCase())) {
    return { isValid: false, error: 'Test email addresses are not allowed' };
  }

  // Format validation with regex
  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Invalid email format. Please use format: user@example.com' };
  }

  // Extract and validate domain structure
  const parts = trimmed.split('@');
  if (parts.length !== 2) {
    return { isValid: false, error: 'Invalid email format' };
  }

  const [localPart, domain] = parts;

  // 🔹 Limit local-part length (before @)
  if (localPart.length > 64) {
    return {
      isValid: false,
      error: 'The part before @ cannot exceed 64 characters',
    };
  }

  // Domain must not be empty
  if (!domain || domain.length === 0) {
    return { isValid: false, error: 'Invalid email format. Domain is missing after @' };
  }

  // Domain must not start or end with dot or hyphen (basic sanity check)
  if (
    domain.startsWith('.') ||
    domain.endsWith('.') ||
    domain.startsWith('-') ||
    domain.endsWith('-')
  ) {
    return {
      isValid: false,
      error: 'Invalid domain format. Domain cannot start or end with dot or hyphen',
    };
  }

  // Accept any domain after @ - no further validation
  return { isValid: true };
};

/**
 * EmailInput - A reusable email input component with format validation
 */
const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  onValidityChange,
  placeholder = 'user@example.com',
  className = '',
  required = false,
  disabled = false,
  error: externalError,
  autoFocus = false,
  onBlur,
  onFocus,
  id,
  name,
  validateOnBlur = true,
  title,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [internalError, setInternalError] = useState<string | undefined>();
  const [isValid, setIsValid] = useState(false);

  // Sync with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  /**
   * Handle input change
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);

    // Client-side format validation
    const formatValidation = validateEmailFormat(newValue);
    if (!formatValidation.isValid) {
      setInternalError(formatValidation.error);
      setIsValid(false);
      onValidityChange?.(false);
    } else {
      // Format is valid
      setInternalError(undefined);
      setIsValid(true);
      onValidityChange?.(true);
    }
  };

  /**
   * Handle blur event
   */
  const handleBlur = () => {
    if (!validateOnBlur) {
      onBlur?.();
      return;
    }

    const trimmed = localValue.trim();
    const formatValidation = validateEmailFormat(trimmed);

    if (!formatValidation.isValid) {
      setInternalError(formatValidation.error);
      setIsValid(false);
      onValidityChange?.(false);
    } else if (trimmed) {
      setInternalError(undefined);
      setIsValid(true);
      onValidityChange?.(true);
    }

    onBlur?.();
  };

  /**
   * Handle focus event
   */
  const handleFocus = () => {
    onFocus?.();
  };

  // Use external error if provided, otherwise use internal validation error
  const displayError = externalError || internalError;
  const showError = !!displayError;
  const showSuccess = isValid && !displayError && localValue.trim();

  // Merge className with error/success states
  const baseClassName = `w-full px-3 py-2 pr-10 rounded-lg border bg-white focus:outline-none focus:ring-2 transition-all break-all ${
    showError
      ? 'border-red-400 focus:ring-red-300 focus:border-red-500'
      : showSuccess
      ? 'border-green-400 focus:ring-green-300 focus:border-green-500'
      : 'border-gray-300 focus:ring-blue-300 focus:border-blue-500'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`;

  return (
    <div className="w-full">
      <div className="relative">
        <input
          type="email"
          id={id}
          name={name}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={baseClassName}
          required={required}
          disabled={disabled}
          readOnly={disabled}
          autoFocus={autoFocus}
          inputMode="email"
          dir="ltr"
          style={{ textAlign: 'left', wordBreak: 'break-all' }}
          title={title}
          maxLength={254} // 🔹 prevent extremely long emails
        />

        {/* Icon indicators */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center gap-1">
          {showSuccess && <FiCheckCircle className="w-5 h-5 text-green-500" />}
          {showError && <FiAlertCircle className="w-5 h-5 text-red-500" />}
          {!showSuccess && !showError && <FiMail className="w-5 h-5 text-gray-400" />}
        </div>
      </div>

      {/* Error message */}
      {showError && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1 break-all">
          <FiAlertCircle className="w-3 h-3" />
          {displayError}
        </p>
      )}

      {/* Success message (optional, subtle) */}
      {showSuccess && (
        <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
          <FiCheckCircle className="w-3 h-3" />
          Valid email
        </p>
      )}
    </div>
  );
};

export default EmailInput;